// Anexo dentro do balão.
//
// O arquivo mora no bucket PRIVADO `inbox-media`, então nada aqui pode usar a
// URL do storage direto: é preciso pedir uma URL assinada, que expira. Só
// pedimos quando o balão aparece na tela — assinar tudo de uma vez numa thread
// longa seria uma rajada de chamadas para arquivos que ninguém vai abrir.
//
// Quando a assinatura falha (arquivo ainda não baixado pelo worker de mídia, ou
// apagado) o balão continua mostrando nome e tamanho. Falhar em silêncio, com
// um espaço vazio, faria o atendente achar que a mensagem veio quebrada.

import React, { useEffect, useState } from 'react';
import { Download, FileText, ImageOff } from 'lucide-react';
import type { Anexo } from '../tipos';
import { tamanhoLegivel } from '../formato';
import { urlAssinada } from '../api';

interface Props {
  anexo: Anexo;
  meu: boolean;
  /** Figurinha: só a imagem, pequena e sem moldura, como no app. */
  figurinha?: boolean;
}

function AnexoBase({ anexo, meu, figurinha = false }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    setUrl(null);
    setFalhou(false);
    // Assinada pelo servidor (aba da negociação): usa direto.
    if (anexo.url) {
      setUrl(anexo.url);
      return () => { vivo = false; };
    }
    urlAssinada(anexo.storage_path)
      .then((u) => {
        if (!vivo) return;
        if (u) setUrl(u);
        else setFalhou(true);
      })
      .catch(() => vivo && setFalhou(true));
    return () => { vivo = false; };
  }, [anexo.storage_path, anexo.url]);

  const mime = anexo.mime_type ?? '';
  const ehImagem = mime.startsWith('image/');
  const ehAudio = mime.startsWith('audio/');
  const ehVideo = mime.startsWith('video/');

  if (figurinha && url && !falhou) {
    return (
      <img
        src={url}
        alt="Figurinha"
        onError={() => setFalhou(true)}
        className="h-32 w-32 object-contain drop-shadow-sm"
      />
    );
  }

  if (ehImagem && url && !falhou) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg">
        <img
          src={url}
          alt={anexo.file_name ?? 'Imagem recebida'}
          onError={() => setFalhou(true)}
          className="max-h-72 w-full cursor-zoom-in object-cover transition-opacity hover:opacity-90"
        />
      </a>
    );
  }

  if (ehAudio && url && !falhou) {
    return <audio controls src={url} className="w-64 max-w-full" />;
  }

  if (ehVideo && url && !falhou) {
    return <video controls src={url} className="max-h-72 w-full rounded-lg" />;
  }

  const corFundo = meu ? 'bg-white/15' : 'bg-gray-200/60 dark:bg-slate-700/60';
  const corTexto = meu ? 'text-white' : 'text-gray-700 dark:text-slate-200';
  const corSecundaria = meu ? 'text-white/70' : 'text-gray-500 dark:text-slate-400';

  const conteudo = (
    <div className={`flex items-center gap-2.5 rounded-lg ${corFundo} px-3 py-2`}>
      <div className={`shrink-0 ${corSecundaria}`}>
        {falhou ? <ImageOff size={18} /> : <FileText size={18} />}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`truncate text-xs font-medium ${corTexto}`}>
          {anexo.file_name ?? 'Arquivo'}
        </p>
        <p className={`text-[11px] ${corSecundaria}`}>
          {falhou ? 'arquivo indisponível' : tamanhoLegivel(anexo.size_bytes) || 'baixando…'}
        </p>
      </div>
      {url && !falhou && <Download size={15} className={`shrink-0 ${corSecundaria}`} />}
    </div>
  );

  if (url && !falhou) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block transition-opacity hover:opacity-90">
        {conteudo}
      </a>
    );
  }
  return conteudo;
}

// Memoizado junto com o balão: sem isso, um render do pai remonta o efeito que
// assina a URL e o anexo pisca a cada atualização da tela.
export default React.memo(AnexoBase, (a, b) => a.anexo.id === b.anexo.id && a.anexo.url === b.anexo.url && a.meu === b.meu && a.figurinha === b.figurinha);
